/** Stable dialog shell. The form is downloaded only after this instance opens. */
import React, { Suspense, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useTrialSourceLocation } from '@/contexts/tuitionSubjectContext';
import { lazyWithChunkRetry } from '@/utils/lazyWithChunkRetry';

export type ProUpsellDialogProps = {
  open: boolean;
  onClose: () => void;
  sourceLocation?: string;
  postSubmitDelayMs?: number;
  successAnimationDelayMs?: number;
};

const createContent = () =>
  lazyWithChunkRetry(() => import('./ProUpsellDialogContent'));

class ContentBoundary extends React.Component<
  {
    children: React.ReactNode;
    onRetry: () => void;
    onClose: () => void;
  },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <DialogContent>
        <Typography role="alert">
          The form could not load. Please try again.
        </Typography>
        <Button onClick={this.props.onRetry}>Try again</Button>
        <Button onClick={this.props.onClose}>Close</Button>
      </DialogContent>
    );
  }
}

export default function ProUpsellDialog(props: ProUpsellDialogProps) {
  const sourceLocation = useTrialSourceLocation(props.sourceLocation);
  const fullScreen = useMediaQuery('(max-width:600px)');
  const [hasOpened, setHasOpened] = useState(props.open);
  const [Content, setContent] = useState(createContent);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (props.open) setHasOpened(true);
  }, [props.open]);

  return (
    <Dialog
      open={props.open}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      keepMounted={hasOpened}
      PaperProps={{
        'aria-label': 'Book a trial class',
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: 'hidden',
          position: 'relative',
        },
      }}
    >
      {(props.open || hasOpened) && (
        <ContentBoundary
          key={attempt}
          onClose={props.onClose}
          onRetry={() => {
            setContent(createContent());
            setAttempt(value => value + 1);
          }}
        >
          <Suspense
            fallback={
              <DialogContent>
                <Stack spacing={2}>
                  <Typography role="status" aria-live="polite">
                    Loading form…
                  </Typography>
                  <Button onClick={props.onClose}>Close</Button>
                </Stack>
              </DialogContent>
            }
          >
            <Content {...props} sourceLocation={sourceLocation} />
          </Suspense>
        </ContentBoundary>
      )}
    </Dialog>
  );
}
